// Drawing the price sticker pages. The same drawing is used for the preview and for the PDF, so what you see is what prints.
// Sizes are measured from the letters' actual ink (not the font's nominal box), so the text uses the sticker's full height and
// still stays at least 3 mm inside every edge.
import { fontCss, fontOf, heightMm, PADDING_MM, PT_PER_MM, PX_PER_MM, ROLL_WIDTH_MM, type FontKey, type StickerSettings } from '../model/options';
import { cellWidthMm, type Sticker } from './layout';

const LINE_PITCH = 1.12; // distance between the baselines of a sticker's lines, × the font size
const SAFETY_PX = 1;     // a hair of extra room so rounding can never put ink in a margin

// Points (what the size and spacing controls use) to drawing pixels.
export const ptToPx = (pt: number) => (pt / PT_PER_MM) * PX_PER_MM;
export const pxToPt = (px: number) => (px / PX_PER_MM) * PT_PER_MM;

type Ctx = CanvasRenderingContext2D;
const supportsSpacing = (ctx: Ctx) => 'letterSpacing' in ctx;

interface LineBox { width: number; left: number; right: number; ascent: number; descent: number }

// The ink box of one line of text: how far it reaches left/right of where it is drawn, and above/below its baseline.
function measureLine(ctx: Ctx, text: string, spacingPx: number): LineBox {
  if (spacingPx === 0 || supportsSpacing(ctx)) {
    ctx.letterSpacing = `${spacingPx}px`;
    const m = ctx.measureText(text);
    return { width: m.actualBoundingBoxLeft + m.actualBoundingBoxRight, left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight, ascent: m.actualBoundingBoxAscent, descent: m.actualBoundingBoxDescent };
  }
  // browsers without canvas letter spacing: letters are placed one by one
  let x = 0; let ascent = 0; let descent = 0; let left = 0; let right = 0;
  [...text].forEach((ch, i) => {
    const m = ctx.measureText(ch);
    if (i === 0) left = m.actualBoundingBoxLeft;
    right = x + m.actualBoundingBoxRight;
    ascent = Math.max(ascent, m.actualBoundingBoxAscent); descent = Math.max(descent, m.actualBoundingBoxDescent);
    x += m.width + spacingPx;
  });
  return { width: left + right, left, right, ascent, descent };
}

function fillLine(ctx: Ctx, text: string, x: number, y: number, spacingPx: number) {
  if (spacingPx === 0 || supportsSpacing(ctx)) { ctx.letterSpacing = `${spacingPx}px`; ctx.fillText(text, x, y); return; }
  for (const ch of text) { ctx.fillText(ch, x, y); x += ctx.measureText(ch).width + spacingPx; }
}

// Lines of a sticker at a given size: each line's box, and the whole block's ink height.
function layoutSticker(ctx: Ctx, lines: Sticker, font: FontKey, px: number, spacingPx: number) {
  ctx.font = fontCss(font, px);
  const boxes = lines.map((l) => measureLine(ctx, l, spacingPx));
  const pitch = px * LINE_PITCH;
  const first = boxes[0]!; const last = boxes[boxes.length - 1]!;
  const height = first.ascent + (lines.length - 1) * pitch + last.descent;
  const width = Math.max(...boxes.map((b) => b.width));
  return { boxes, pitch, height, width };
}

// The inside of one sticker, in drawing pixels, once the 3 mm margin is taken off every edge.
export function innerBox(s: Pick<StickerSettings, 'columns' | 'height'>) {
  const pad = PADDING_MM * PX_PER_MM + SAFETY_PX;
  return { w: cellWidthMm(s.columns) * PX_PER_MM - 2 * pad, h: heightMm(s.height) * PX_PER_MM - 2 * pad };
}

const scratch = () => document.createElement('canvas').getContext('2d')!;

// Does this sticker fit inside its margins at this size?
export function fits(lines: Sticker, s: StickerSettings, px: number): boolean {
  const ctx = scratch();
  const { w, h } = innerBox(s);
  const b = layoutSticker(ctx, lines, s.font, px, ptToPx(s.spacingPt));
  return b.width <= w && b.height <= h;
}

// The largest font size (in pixels) at which every given sticker fits inside its margins.
export function autoSizePx(stickers: Sticker[], s: StickerSettings): number {
  const ctx = scratch();
  const { w, h } = innerBox(s);
  const spacing = ptToPx(s.spacingPt);
  const all = stickers.length ? stickers : [['$88.88']]; // nothing typed yet: size for a typical price
  const ok = (px: number) => all.every((lines) => { const b = layoutSticker(ctx, lines, s.font, px, spacing); return b.width <= w && b.height <= h; });
  let lo = 1; let hi = h * 3;
  for (let i = 0; i < 30; i++) { const mid = (lo + hi) / 2; if (ok(mid)) lo = mid; else hi = mid; }
  return Math.floor(lo * 10) / 10;
}

// The Auto size for a sticker height: the largest size at which a line of digits and "$" fills the height inside the
// margins. Every sticker is drawn at this size unless its text is too long, then just small enough to fit.
const HEIGHT_REFERENCE = '$0123456789';
export function heightSizePx(s: StickerSettings): number {
  const ctx = scratch();
  const { h } = innerBox(s);
  let lo = 1; let hi = h * 3;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (layoutSticker(ctx, [HEIGHT_REFERENCE], s.font, mid, 0).height <= h) lo = mid; else hi = mid;
  }
  return Math.floor(lo * 10) / 10;
}

// The size each sticker is drawn at: the manual size; or on Auto, the height's size, made smaller only for a sticker whose
// text would not fit otherwise (long prices, two lines).
export function sizesPx(stickers: Sticker[], s: StickerSettings): number[] {
  if (!s.autoSize) return stickers.map(() => ptToPx(s.sizePt));
  const max = heightSizePx(s);
  return stickers.map((st) => (fits(st, s, max) ? max : Math.min(max, autoSizePx([st], s))));
}

// Waits until the chosen font is ready (the brand font comes from Adobe Fonts; the others are part of the app).
export async function loadFont(font: FontKey, sample: string) {
  try { await document.fonts.load(fontCss(font, 40), sample || '$0123456789'); } catch { /* the browser falls back to another font */ }
}

// Draws one page (one piece of the roll) onto `canvas`: white, the stickers' text in black, centred, and, with 2 columns,
// an optional light dashed cut line between them. Text larger than its sticker (manual size) is cut off at the sticker's edge.
// `scale` draws a smaller copy (the on-screen preview); the PDF uses the full 600 dots per inch.
export function drawPage(canvas: HTMLCanvasElement, page: Sticker[], s: StickerSettings, px: number[], scale = 1) {
  const cellW = cellWidthMm(s.columns) * PX_PER_MM;
  const cellH = heightMm(s.height) * PX_PER_MM;
  canvas.width = Math.round(ROLL_WIDTH_MM * PX_PER_MM * scale);
  canvas.height = Math.round(cellH * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, ROLL_WIDTH_MM * PX_PER_MM, cellH);
  const spacing = ptToPx(s.spacingPt);
  page.forEach((lines, col) => {
    const x0 = col * cellW;
    const b = layoutSticker(ctx, lines, s.font, px[col]!, spacing);
    ctx.save();
    ctx.beginPath(); ctx.rect(x0, 0, cellW, cellH); ctx.clip();
    ctx.fillStyle = '#000';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    let baseline = (cellH - b.height) / 2 + b.boxes[0]!.ascent;
    lines.forEach((text, i) => {
      const box = b.boxes[i]!;
      const x = x0 + cellW / 2 + (box.left - box.right) / 2; // centres the line's ink
      fillLine(ctx, text, x, baseline, spacing);
      baseline += b.pitch;
    });
    ctx.restore();
  });
  if (s.columns === 2 && s.cutLine) {
    // a very light dotted line down the middle, to cut along with scissors
    ctx.strokeStyle = '#c4c4c4';
    ctx.lineWidth = Math.max(1, PX_PER_MM * 0.12);
    ctx.lineCap = 'round';
    ctx.setLineDash([0.01, PX_PER_MM * 0.8]);
    ctx.beginPath(); ctx.moveTo(cellW, 0); ctx.lineTo(cellW, cellH); ctx.stroke();
  }
}

export const fontLabel = (font: FontKey) => fontOf(font).label;
